import React from 'react';
import { useTheme } from 'next-themes';
import {
  Box,
  Flex,
  Heading,
  Button,
  IconButton,
  Spacer,
  Progress,
  Text,
} from '@chakra-ui/react';
import { MoonIcon, SunIcon } from '@chakra-ui/icons';

interface HeaderProps {
  apiUsage?: {
    used: number;
    limit: number;
    remaining: number;
  } | null;
  currentPlan?: 'free' | 'premium';
}

const Header: React.FC<HeaderProps> = ({ apiUsage, currentPlan = 'free' }) => {
  const { theme, setTheme } = useTheme();
  const isDark = theme === 'dark';

  // テーマ対応
  const headerBg = isDark ? 'gray.800' : '#2D3748';
  const headerColor = isDark ? 'gray.100' : 'white';
  const progressBg = isDark ? 'gray.600' : 'whiteAlpha.300';
  const buttonBorderColor = isDark ? 'gray.400' : 'whiteAlpha.600';
  const buttonHoverBorderColor = isDark ? 'gray.200' : 'whiteAlpha.800';
  const hoverBg = isDark ? 'gray.700' : 'whiteAlpha.200';

  // API使用量のパーセンテージを計算
  const usagePercentage = apiUsage ? Math.round((apiUsage.used / apiUsage.limit) * 100) : 0;

  return (
    <Box as="header" bg={headerBg} color={headerColor} px={4} py={2}>
      <Flex maxW="container.xl" mx="auto" align="center">
        <Heading as="h1" size="md" fontFamily="Highlander, cursive" fontWeight="400">
          BrainDump
        </Heading>
        
        <Spacer />
        
        <Flex gap={4} align="center">
          {apiUsage && (
            <Box>
              <Text fontSize="xs" mb={1}>API使用量: {usagePercentage}%</Text>
              <Progress 
                value={usagePercentage} 
                size="xs" 
                width="100px" 
                colorScheme={usagePercentage > 80 ? "red" : usagePercentage > 50 ? "yellow" : "green"} 
                borderRadius="full"
                bg={progressBg}
              />
            </Box>
          )}
          
          <Button 
            size="sm" 
            variant="outline" 
            borderColor={buttonBorderColor}
            color={headerColor}
            _hover={{
              borderColor: buttonHoverBorderColor,
              bg: hoverBg
            }}
          >
            {currentPlan === 'premium' ? 'プレミアムプラン' : '無料プラン'}
          </Button>
          
          <IconButton
            aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
            icon={isDark ? <SunIcon /> : <MoonIcon />}
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
            variant="ghost"
            color={headerColor}
            _hover={{
              bg: hoverBg
            }}
            size="sm"
          />
        </Flex>
      </Flex>
    </Box>
  );
};

export default Header;
